import {Component, ConfigOptions} from 'panel';

import {getCookie} from 'common/util/cookie';
import {compoundSentence} from 'common/util/string';

import {track, TrackEvents, TrackProperties} from '../tracking';
import template from './index.jade';
import {FormErrors, FormField, FormValues, getAllValidationErrors, PasswordLevel} from './validation';

interface ResetPasswordState {
  formValues: FormValues;
  formErrors: FormErrors;
}

export class ResetPassword extends Component<ResetPasswordState> {
  private form: HTMLFormElement;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      valid: {type: `boolean`, description: `whether the reset password link is still valid`},
      errors: {type: `json`, description: `list of errors shown in API`},
      message: {type: `string`, description: `notice from API, shown in banner`},
      'password-level': {
        type: `number`,
        description: `level of password security required`,
        enum: Object.values(PasswordLevel) as string[],
      },
    };
  }

  get config(): ConfigOptions<ResetPasswordState> {
    return {
      template,
      defaultState: {
        formValues: {
          password: ``,
          confirm: ``,
        },
        formErrors: {
          password: null,
          confirm: null,
        },
      },
      helpers: {
        FormField,
        onFormInserted: ({elm}) => {
          this.form = elm;
        },

        handlePasswordInput: (field: FormField, ev) => {
          const {value} = ev.target;
          this.update({
            formValues: {
              ...this.state.formValues,
              [field]: value,
            },
          });
        },

        handlePasswordBlur: () => {
          this.validate();
        },

        handleSubmit: (ev) => {
          this.validate() || ev.preventDefault();
        },

        handleSubmitClick: () => {
          this.validate() && this.form.submit();
        },

        getApiErrors: () => {
          const errors = this.getJSONAttribute(`errors`);
          if (!(errors && errors.length)) {
            return null;
          }

          return `Your new password ${compoundSentence(errors)}.`;
        },

        getCsrfToken: () => {
          return getCookie(`csrftoken`);
        },
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();

    track(TrackEvents.ViewResetPasswordPage, {
      [TrackProperties.PageVersion]: `Reset Password Page`,
    });
  }

  private validate(): boolean {
    const formErrors = getAllValidationErrors(this.state.formValues, this.attr(`password-level`));
    this.update({formErrors});
    return !Object.values(formErrors).some(Boolean);
  }
}

customElements.define(`mp-reset-password`, ResetPassword);
